import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import FaqIcons from '../../../public/faq/FaqIcons.json';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { useState } from 'react';
import { Button } from '@/components/ui/button';

type Props<T extends {}> = {
	label?: string;
	placeholder?: string;
	optional?: boolean;
} & FormInputProps<T>;

const EFaqIconsInput = <T extends {}>({
	label,
	placeholder = 'Choose icon',
	optional = false,
	fieldName,
	control,
	containerClassName,
}: Props<T>) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Controller
			name={fieldName}
			control={control}
			render={({ field: { value, onChange }, fieldState }) => {
				const [title, url] = (value as string)?.split('-') ?? [];

				const handleClick = (category: string, url: string) => {
					onChange(`${category}-${url}`);
					setIsOpen(false);
				};

				return (
					<DropdownMenu
						open={isOpen}
						onOpenChange={setIsOpen}>
						<div
							className={cn(
								'w-full flex flex-col gap-1.5 select-none',
								containerClassName,
							)}>
							{label && (
								<h4 className='text-sm font-medium'>
									{label}
									{optional && (
										<span className='text-popover-600'>
											{' '}
											(optional)
										</span>
									)}
								</h4>
							)}
							<DropdownMenuTrigger
								asChild
								className={'px-4 p-3 rounded-semi-xl'}>
								<Button
									variant='outline'
									className={cn(
										'gap-2.5 max-h-fit',
										fieldState.invalid
											? 'border-error-1000'
											: '',
									)}>
									{value && (
										<>
											<img
												src={url}
												className='size-5'
											/>
											<span className='text-xs'>
												{title}
											</span>
										</>
									)}
									{!value && (
										<span className='text-xs text-popover-600'>
											{placeholder}
										</span>
									)}
									<div
										className={`ml-auto transition-transform duration-50 ease-in-out ${
											isOpen ? '-rotate-180' : 'rotate-0'
										}`}>
										{<ArrowDownIcon />}
									</div>
								</Button>
							</DropdownMenuTrigger>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</div>
						<DropdownMenuContent
							className={
								'w-(--radix-dropdown-menu-trigger-width) max-h-56 h-fit px-4 flex flex-col gap-2 rounded-2.5xl border border-popover-100 bg-popover-10 shadow-custom overflow-y-scroll custom-scrollbar'
							}>
							<>
								<div className='flex flex-col gap-2 select-none'>
									{Object.entries(FaqIcons).map(
										([category, icons]) => (
											<div
												key={category}
												className='py-3 flex flex-col gap-2'>
												<h3 className='text-xs text-popover-600'>
													{category}
												</h3>
												<div className='flex items-center gap-2'>
													{Object.entries(icons).map(
														([key, iconUrl]) => (
															<div
																key={key}
																className={cn(
																	'h-8 p-1.5 rounded-sm hover:bg-primary-color-100 cursor-pointer',
																	url ===
																		iconUrl &&
																		'outline outline-popover-1000',
																)}
																onClick={() =>
																	handleClick(
																		category,
																		iconUrl,
																	)
																}>
																<img
																	src={
																		iconUrl
																	}
																	alt='faq icon'
																	className='size-5'
																/>
															</div>
														),
													)}
												</div>
											</div>
										),
									)}
								</div>
							</>
						</DropdownMenuContent>
					</DropdownMenu>
				);
			}}
		/>
	);
};

export default EFaqIconsInput;
